// The whole client side of a room, from connecting to sitting at a finished table. It owns the
// connection, the session token and the phase, and turns server messages into events the UI
// listens to. Mirror of OnlineGameActivity on Android, minus the rendering.
//
// The server is authoritative: nothing here decides anything about the game, it only routes.

import { Connection } from './connection.js';
import { ask } from './request.js';
import { Protocol, ServerProtocol } from '../generated/protocol.js';

export const Phase = Object.freeze({
  IDLE: 'idle',
  CONNECTING: 'connecting',
  LOBBY: 'lobby',
  PLAYING: 'playing',
  FINISHED: 'finished',
});

const MaxReconnectAttempts = 5;
const ReconnectDelayMillis = 1500;

export class Session extends EventTarget {
  #connection = null;
  #serverUrl = null;
  #phase = Phase.IDLE;
  #pendingAction = null;
  #reconnecting = false;
  #reconnectAttempts = 0;
  #reconnectTimer = null;

  playerToken = null;
  roomId = null;
  myPosition = -1;
  playersNames = [];
  // True between sync_begin and sync_end, while the server replays our personal history: the UI
  // must rebuild itself silently instead of animating a game it already watched
  syncing = false;

  get phase() {
    return this.#phase;
  }

  #emit(type, detail) {
    this.dispatchEvent(new CustomEvent(type, { detail }));
  }

  #setPhase(phase) {
    this.#phase = phase;
    this.#emit('phase', { phase });
  }

  // Asks the server for the catalogue on a throwaway connection: the lobby form needs it before
  // any room exists. Resolves with { games, difficulties, emotes }
  static fetchCatalogue(serverUrl, timeoutMillis = 8000) {
    return ask(
      serverUrl,
      { [Protocol.JSONTagID]: ServerProtocol.TagListGames },
      ServerProtocol.TagGamesCatalog,
      timeoutMillis,
    ).then((message) => ({
      games: message[ServerProtocol.GamesID] ?? [],
      difficulties: message[ServerProtocol.DifficultiesID] ?? [],
      emotes: message[ServerProtocol.EmotesID] ?? [],
      // Whether this server can send mail at all. The account screen offers recovery by link
      // only where there is something behind it
      emailEnabled: message[ServerProtocol.EmailEnabledID] === true,
      // The whole message as well: the card and auction vocabulary is read straight from it, and
      // listing its sections here would be one more thing to keep in step
      raw: message,
    }));
  }

  // The history of whoever is asking, outside any room. Signed in that is the account; signed
  // out it is still this device's old token, so a guest keeps seeing their own games
  static fetchStats(serverUrl, accountToken, sessionToken, timeoutMillis = 8000) {
    return ask(
      serverUrl,
      {
        [Protocol.JSONTagID]: ServerProtocol.TagStats,
        [ServerProtocol.AccountTokenID]: accountToken,
        [ServerProtocol.SessionTokenID]: sessionToken ?? '',
      },
      ServerProtocol.TagStatsResult,
      timeoutMillis,
    );
  }

  // config: { serverUrl, playerName, accountToken, sessionToken, gameType, deckType, deckFormat,
  // playersNumber, difficulty }
  createRoom(config) {
    this.#start(config.serverUrl, {
      [Protocol.JSONTagID]: ServerProtocol.TagCreateRoom,
      [Protocol.JSONPlayerNameID]: config.playerName,
      [ServerProtocol.AccountTokenID]: config.accountToken,
      [ServerProtocol.SessionTokenID]: config.sessionToken ?? '',
      [Protocol.JSONGameTypeID]: config.gameType,
      [Protocol.JSONDeckTypeID]: config.deckType,
      [Protocol.JSONDeckFormatID]: config.deckFormat,
      [Protocol.JSONPlayersNumberID]: config.playersNumber,
      [Protocol.JSONAIDifficultyID]: config.difficulty,
    });
  }

  joinRoom(config) {
    this.#start(config.serverUrl, {
      [Protocol.JSONTagID]: ServerProtocol.TagJoinRoom,
      [Protocol.JSONPlayerNameID]: config.playerName,
      [ServerProtocol.AccountTokenID]: config.accountToken,
      [ServerProtocol.SessionTokenID]: config.sessionToken ?? '',
      [ServerProtocol.RoomIdID]: config.roomId.toUpperCase(),
    });
  }

  #start(serverUrl, firstMessage) {
    this.#serverUrl = serverUrl;
    this.#pendingAction = firstMessage;
    this.#reconnecting = false;
    this.#reconnectAttempts = 0;
    this.#setPhase(Phase.CONNECTING);
    this.#connect();
  }

  #connect() {
    try {
      this.#connection = new Connection(this.#serverUrl, {
        onOpen: () => {
          if (this.#reconnecting) {
            this.#connection.send({
              [Protocol.JSONTagID]: ServerProtocol.TagReconnect,
              [ServerProtocol.PlayerTokenID]: this.playerToken,
            });
          } else {
            this.#connection.send(this.#pendingAction);
          }
        },
        onMessage: (message) => this.#handle(message),
        onDisconnected: () => this.#handleDisconnected(),
      });
    } catch (error) {
      this.#emit('fatal', { message: `indirizzo del server non valido: ${error.message}` });
      this.#setPhase(Phase.FINISHED);
    }
  }

  #handle(message) {
    const tag = message[Protocol.JSONTagID];
    switch (tag) {
      case ServerProtocol.TagRoomCreated:
      case ServerProtocol.TagRoomJoined:
        this.roomId = message[ServerProtocol.RoomIdID];
        this.playerToken = message[ServerProtocol.PlayerTokenID];
        this.myPosition = message[ServerProtocol.YourPositionID];
        this.#reconnectAttempts = 0;
        this.#setPhase(Phase.LOBBY);
        break;

      case ServerProtocol.TagLobbyState:
        this.playersNames = message[Protocol.JSONPlayersArrayID] ?? [];
        this.#emit('lobby', {
          players: this.playersNames,
          registered: message[ServerProtocol.RegisteredID] ?? [],
          seats: message[Protocol.JSONPlayersNumberID] ?? this.playersNames.length,
          roomName: message[Protocol.JSONGameNameID] ?? '',
          deckType: message[Protocol.JSONDeckTypeID],
        });
        break;

      case ServerProtocol.TagGameStarting:
        this.myPosition = message[ServerProtocol.YourPositionID];
        this.playersNames = message[Protocol.JSONPlayersArrayID] ?? [];
        this.#setPhase(Phase.PLAYING);
        this.#emit('game-start', {
          players: this.playersNames,
          myPosition: this.myPosition,
          gameType: message[Protocol.JSONGameTypeID],
          deckType: message[Protocol.JSONDeckTypeID],
          deckFormat: message[Protocol.JSONDeckFormatID],
          syncing: this.syncing,
        });
        break;

      case Protocol.JSONNotifyObjectTag:
        this.#emit('game-message', {
          object: message[Protocol.JSONObjectID],
          syncing: this.syncing,
        });
        break;

      case Protocol.JSONNotifyObjectWithResponseTag:
        this.#emit('request', {
          object: message[Protocol.JSONObjectID],
          position: message[Protocol.JSONPlayerPositionID],
          forMe: message[Protocol.JSONPlayerPositionID] === this.myPosition,
          syncing: this.syncing,
        });
        break;

      case ServerProtocol.TagSyncBegin:
        this.syncing = true;
        this.myPosition = message[ServerProtocol.YourPositionID] ?? this.myPosition;
        this.#emit('sync', { syncing: true });
        break;

      case ServerProtocol.TagSyncEnd:
        this.syncing = false;
        this.#emit('sync', { syncing: false });
        this.#emit('notice', { text: 'Riconnesso alla partita' });
        break;

      case ServerProtocol.TagChatMessage:
        this.#emit('chat', { message, live: true });
        break;

      case ServerProtocol.TagChatHistory:
        this.#emit('chat-history', { messages: message[ServerProtocol.ChatMessagesID] ?? [] });
        break;

      case ServerProtocol.TagPlayerDisconnected:
        this.#emit('notice', { text: `${this.#nameAt(message)} si è disconnesso...` });
        break;

      case ServerProtocol.TagPlayerReconnected:
        this.#emit('notice', { text: `${this.#nameAt(message)} è tornato` });
        break;

      case ServerProtocol.TagRoomClosed:
        this.#emit('fatal', { message: 'La stanza è stata chiusa' });
        this.leave(false);
        break;

      case Protocol.JSONExitGameTag:
        this.#emit('fatal', { message: 'Partita terminata: giocatori insufficienti' });
        this.leave(false);
        break;

      case ServerProtocol.TagError:
        this.#handleError(message);
        break;

      default:
        // Unknown tags are future protocol additions: ignoring them keeps old clients working
        break;
    }
  }

  #handleError(message) {
    const description = message[ServerProtocol.MessageID] ?? 'errore sconosciuto';
    const inResponseTo = message[ServerProtocol.InResponseToID] ?? '';
    // An error on the commands that establish the session leaves nothing to stay for
    const fatal =
      inResponseTo === ServerProtocol.TagCreateRoom ||
      inResponseTo === ServerProtocol.TagJoinRoom ||
      inResponseTo === ServerProtocol.TagReconnect;
    this.#emit(fatal ? 'fatal' : 'notice', { text: description, message: description });
    if (fatal) {
      this.leave(false);
    }
  }

  #handleDisconnected() {
    if (this.#phase === Phase.FINISHED) {
      return;
    }
    // The server keeps a disconnected seat alive for its grace period and can replay the history,
    // in the lobby as well as mid-game, so both are worth retrying
    const resumable = this.#phase === Phase.PLAYING || this.#phase === Phase.LOBBY;
    if (resumable && this.playerToken && this.#reconnectAttempts < MaxReconnectAttempts) {
      this.#reconnectAttempts += 1;
      this.#emit('notice', {
        text: `Connessione persa, riconnessione (${this.#reconnectAttempts}/${MaxReconnectAttempts})...`,
      });
      this.#reconnecting = true;
      this.#reconnectTimer = setTimeout(() => {
        if (this.#phase !== Phase.FINISHED) {
          this.#connect();
        }
      }, ReconnectDelayMillis);
      return;
    }
    this.#emit('fatal', {
      message:
        this.#phase === Phase.CONNECTING
          ? 'Impossibile connettersi al server'
          : 'Connessione col server persa',
    });
    this.#setPhase(Phase.FINISHED);
  }

  #nameAt(message) {
    const position = message[Protocol.JSONPlayerPositionID] ?? -1;
    return this.playersNames[position] ?? 'Un giocatore';
  }

  startGame() {
    this.#connection?.send({ [Protocol.JSONTagID]: ServerProtocol.TagStartRoomGame });
  }

  respond(response) {
    this.#connection?.send({
      [Protocol.JSONTagID]: Protocol.JSONGeneralResponseTag,
      [Protocol.JSONResponseID]: response,
    });
  }

  sendChatText(text) {
    this.#connection?.send({
      [Protocol.JSONTagID]: ServerProtocol.TagChat,
      [ServerProtocol.ChatTextID]: text,
    });
  }

  sendChatEmote(emoteName) {
    this.#connection?.send({
      [Protocol.JSONTagID]: ServerProtocol.TagChat,
      [ServerProtocol.ChatEmoteID]: emoteName,
    });
  }

  // announce=false when the server already told us the room is gone
  leave(announce = true) {
    clearTimeout(this.#reconnectTimer);
    if (announce) {
      this.#connection?.send({ [Protocol.JSONTagID]: ServerProtocol.TagLeaveRoom });
    }
    this.#setPhase(Phase.FINISHED);
    this.#connection?.close();
    this.#connection = null;
  }
}
